import React from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft } from "react-icons/fa";

interface BackButtonProps {
  /**
   * The route to navigate to when the back button is clicked.
   */
  fallbackRoute: string;
  /**
   * Optional text to display on the button. Defaults to "Cancel".
   */
  text?: string;
  /**
   * Optional className for styling. Defaults to "back-btn".
   */
  className?: string;
  /**
   * If true, renders only the left arrow icon.
   */
  iconOnly?: boolean;
}

/**
 * A reusable Back Button component that navigates to a listing page
 * and passes the restoreListState flag to ensure the list state is preserved.
 */
const BackButton: React.FC<BackButtonProps> = ({
  fallbackRoute,
  text = "Cancel",
  className = "back-btn",
  iconOnly = false,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    // Navigating explicitly to the fallback route ensures that even if the
    // user arrived via a direct URL, they will correctly navigate to the list page.
    // The restoreListState flag informs the useListState hook to restore the saved state.
    navigate(fallbackRoute, { state: { restoreListState: true } });
  };

  if (iconOnly) {
    return (
      <FaChevronLeft
        size={18}
        onClick={handleBack}
        style={{ cursor: "pointer", marginRight: "10px" }}
        title={text}
      />
    );
  }

  return (
    <button type="button" className={className} onClick={handleBack}>
      {text}
    </button>
  );
};

export default BackButton;
